import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clock4 } from "lucide-react";
import IDENTIFIERS from "@/constants/test_id";
import ScreenRouteNames from "@/constants/screen-route-names";

type SessionExpiredState = {
  path?: string;
  replace?: boolean;
  redirectUrl?: string;
};

const SessionExpiredPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as SessionExpiredState;
  const handleReturnToLogin = () => {
    if (Boolean(state?.redirectUrl) && state.redirectUrl.length > 0) {
      window.location.href = state.redirectUrl;
      return;
    } else {
      navigate(`/${ScreenRouteNames.LOGIN.path}`, { replace: true });
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white text-slate-900 antialiased">
      {/* Main */}
      <main className="flex flex-1 flex-col items-center justify-center px-6 pb-20 text-center">
        {/* Icon */}
        <div
          aria-hidden="true"
          className="relative mb-7 grid h-[72px] w-[72px] place-items-center rounded-xl bg-primary-extraLight"
        >
          <Clock4
            height={40}
            width={40}
            strokeWidth={1.5}
            className="text-primary"
          />
        </div>

        {/* Eyebrow */}
        <div className="mb-3.5 text-xs font-medium uppercase tracking-[0.06em] text-primary">
          Session Expired
        </div>

        {/* Title */}
        <h1 className="mb-3.5 max-w-[22ch] text-3xl font-semibold leading-tight tracking-tight">
          Your session has timed out.
        </h1>

        {/* Body */}
        <p className="mb-9 max-w-[44ch] text-[15px] leading-relaxed text-slate-600">
          For your security, you&apos;ve been signed out after a period of
          inactivity. Please sign in again to continue.
        </p>

        {/* CTA */}
        <Button
          onClick={handleReturnToLogin}
          className="hover:bg-primary-selected"
          size="lg"
          data-testid={IDENTIFIERS.RETURN_LOGIN_BUTTON}
        >
          Return to login
          <span className="font-bold transition-transform duration-200 group-hover:translate-x-1">
            →
          </span>
        </Button>

        {/* Meta */}
        <div className="mt-7 flex flex-col items-center gap-1 text-[12.5px] text-slate-400 sm:flex-row sm:gap-2.5">
          <span>Signed out automatically</span>
          <span className="hidden h-[3px] w-[3px] rounded-full bg-slate-400 sm:block" />
          <span>No data was lost</span>
        </div>
      </main>
    </div>
  );
};

export default SessionExpiredPage;
